import React, { useEffect, useMemo, useState } from 'react';

import { PromiseToPayWithUser, PromiseToPayCreate } from '@dill/dill-shared';
import { useAppSelector, useAppDispatch } from '../../../../redux/store';
import {
  getSupplierPromiseToPays,
  createPromiseToPay,
  deletePromiseToPay,
  cancelPromiseToPay,
  updatePromiseToPay,
} from '../../../../redux/services/promiseToPayService';
import { getSupplierBuyers } from '../../../../redux/services/supplierBuyersService';
import { Plus } from 'react-feather';
import { DillButton, DillModal, DillDropdown } from '../../../general/Dill';
import SupplierPromiseToPaysTable from './SupplierPromiseToPaysTable';
import { PromiseToPayDetailsModal } from '../../customers/CustomerDetails/SupplierCustomerPromiseToPays/PromiseToPayDetailsModal';
import { PromiseToPayFormModal } from '../../customers/CustomerDetails/SupplierCustomerPromiseToPays/PromiseToPayFormModal';

function SupplierPromiseToPays() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { supplierPromiseToPays, loadingList } = useAppSelector((state) => state.promiseToPays);
  const { supplierBuyers, loadingList: buyersLoadingList } = useAppSelector(
    (state) => state.supplierBuyers
  );

  const [searchText, setSearchText] = useState('');
  const [sortOptions, setSortOptions] = useState<{ sortOrder: 'asc' | 'desc'; sortBy: string }>({
    sortOrder: 'desc',
    sortBy: 'promisedPaymentDate',
  });
  const [selectedPromise, setSelectedPromise] = useState<PromiseToPayWithUser | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromise, setEditingPromise] = useState<PromiseToPayWithUser | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  // "+ New Promise" flow: this page lists promises across every customer, so there's no buyer
  // already in scope like there is on a single customer's page - pick one first, then open the
  // same form modal used for editing (in create mode, since editingPromise stays null).
  const [isCustomerPickerOpen, setIsCustomerPickerOpen] = useState(false);
  const [pickerBuyerId, setPickerBuyerId] = useState<string | null>(null);
  const [newPromiseBuyerId, setNewPromiseBuyerId] = useState<string | null>(null);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierPromiseToPays({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, dispatch]);

  useEffect(() => {
    if (currentSupplier?.id && supplierBuyers.length === 0) {
      dispatch(getSupplierBuyers({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, supplierBuyers.length, dispatch]);

  const handleCancel = async (promiseToPayId: string) => {
    if (currentSupplier?.id) {
      await dispatch(
        cancelPromiseToPay({
          supplierId: currentSupplier.id,
          promiseToPayId,
        })
      );
      dispatch(getSupplierPromiseToPays({ supplierId: currentSupplier.id }));
    }
  };

  const handleDelete = async (promiseToPayId: string) => {
    if (currentSupplier?.id) {
      await dispatch(
        deletePromiseToPay({
          supplierId: currentSupplier.id,
          promiseToPayId,
        })
      );
      dispatch(getSupplierPromiseToPays({ supplierId: currentSupplier.id }));
    }
  };

  const handleRowClick = (promise: PromiseToPayWithUser) => {
    setSelectedPromise(promise);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPromise(null);
  };

  const handleEdit = (promise: PromiseToPayWithUser) => {
    setEditingPromise(promise);
    setIsFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingPromise(null);
    setNewPromiseBuyerId(null);
  };

  const handleUpdate = async (data: PromiseToPayCreate) => {
    if (currentSupplier?.id && editingPromise) {
      await dispatch(
        updatePromiseToPay({
          supplierId: currentSupplier.id,
          promiseToPayId: editingPromise.id,
          data,
        })
      );
      setIsFormModalOpen(false);
      setEditingPromise(null);
      dispatch(getSupplierPromiseToPays({ supplierId: currentSupplier.id }));
    }
  };

  const handleCreate = async (data: PromiseToPayCreate) => {
    if (currentSupplier?.id) {
      await dispatch(createPromiseToPay({ supplierId: currentSupplier.id, data }));
      setIsFormModalOpen(false);
      setNewPromiseBuyerId(null);
      dispatch(getSupplierPromiseToPays({ supplierId: currentSupplier.id }));
    }
  };

  const handleSubmitForm = (data: PromiseToPayCreate) =>
    editingPromise ? handleUpdate(data) : handleCreate(data);

  const handleOpenCustomerPicker = () => {
    setPickerBuyerId(null);
    setIsCustomerPickerOpen(true);
  };

  const handleCloseCustomerPicker = () => {
    setIsCustomerPickerOpen(false);
    setPickerBuyerId(null);
  };

  const handleConfirmCustomerPicker = () => {
    if (!pickerBuyerId) return;
    setNewPromiseBuyerId(pickerBuyerId);
    setIsFormModalOpen(true);
    handleCloseCustomerPicker();
  };

  const buyerOptions = useMemo(
    () =>
      supplierBuyers.map((buyer) => ({ label: buyer.name || 'Unnamed customer', value: buyer.id })),
    [supplierBuyers]
  );

  const newPromiseBuyer = useMemo(
    () => supplierBuyers.find((buyer) => buyer.id === newPromiseBuyerId) || null,
    [supplierBuyers, newPromiseBuyerId]
  );

  const filteredPromiseToPays = supplierPromiseToPays
    .filter((promise) => {
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        return (
          (promise.source || '').toLowerCase().includes(searchLower) ||
          (promise.type || '').toLowerCase().includes(searchLower) ||
          (promise.reason || '').toLowerCase().includes(searchLower) ||
          promise.status?.toLowerCase().includes(searchLower) ||
          (promise.buyer?.name || '').toLowerCase().includes(searchLower) ||
          (promise.createdByUser?.firstName || '').toLowerCase().includes(searchLower) ||
          (promise.createdByUser?.lastName || '').toLowerCase().includes(searchLower) ||
          (promise.createdByUser?.email || '').toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let valueA: string | number | boolean | undefined;
      let valueB: string | number | boolean | undefined;

      if (
        sortOptions.sortBy === 'promisedPaymentDate' ||
        sortOptions.sortBy === 'createdAt' ||
        sortOptions.sortBy === 'updatedAt'
      ) {
        valueA = a[sortOptions.sortBy as keyof PromiseToPayWithUser]
          ? new Date(a[sortOptions.sortBy as keyof PromiseToPayWithUser] as Date).getTime()
          : 0;
        valueB = b[sortOptions.sortBy as keyof PromiseToPayWithUser]
          ? new Date(b[sortOptions.sortBy as keyof PromiseToPayWithUser] as Date).getTime()
          : 0;
      } else if (sortOptions.sortBy === 'amount') {
        valueA = a.amount || 0;
        valueB = b.amount || 0;
      } else if (sortOptions.sortBy === 'buyerName') {
        valueA = a.buyer?.name || '';
        valueB = b.buyer?.name || '';
      } else {
        valueA = (a[sortOptions.sortBy as keyof PromiseToPayWithUser] as string) || '';
        valueB = (b[sortOptions.sortBy as keyof PromiseToPayWithUser] as string) || '';
      }

      if (valueA < valueB) return sortOptions.sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOptions.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div className="w-full h-full p-4">
      <div className="flex flex-col overflow-x-scroll h-full w-full pb-2">
        <SupplierPromiseToPaysTable
          promiseToPays={filteredPromiseToPays}
          sortOptions={sortOptions}
          onSortChange={setSortOptions}
          onRowClick={handleRowClick}
          onDelete={handleDelete}
          onCancel={handleCancel}
          onEdit={handleEdit}
          searchValue={searchText}
          onSearch={setSearchText}
          toolbarRight={
            <DillButton
              label="New Promise"
              icon={<Plus size={14} />}
              onClick={handleOpenCustomerPicker}
              height={31}
            />
          }
        />
      </div>

      <PromiseToPayDetailsModal
        open={isModalOpen}
        promise={selectedPromise}
        onClose={handleCloseModal}
        onCancel={handleCancel}
      />

      <PromiseToPayFormModal
        open={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSubmit={handleSubmitForm}
        initialData={editingPromise}
        isLoading={
          loadingList.includes('createPromiseToPay') || loadingList.includes('updatePromiseToPay')
        }
        supplierId={currentSupplier?.id || ''}
        buyerId={editingPromise?.buyerId || newPromiseBuyerId || ''}
        contacts={editingPromise?.buyer?.buyerContacts ?? newPromiseBuyer?.buyerContacts ?? []}
      />

      <DillModal
        open={isCustomerPickerOpen}
        onClose={handleCloseCustomerPicker}
        title="New Promise to Pay"
        subtitle="Pick which customer this promise is for."
        footer={
          <>
            <DillButton label="Cancel" variant="secondary" onClick={handleCloseCustomerPicker} />
            <DillButton
              label="Continue"
              onClick={handleConfirmCustomerPicker}
              isDisabled={!pickerBuyerId}
            />
          </>
        }>
        <DillDropdown
          label="Customer"
          items={buyerOptions}
          value={pickerBuyerId}
          containerClassName="w-full h-[34px]"
          onSelectChange={(item) => setPickerBuyerId(item?.value ?? null)}
          placeholder="Search customers…"
          isLoading={buyersLoadingList.includes('getSupplierBuyers')}
        />
      </DillModal>
    </div>
  );
}

export default SupplierPromiseToPays;
